import { useMemo, useState } from 'react';

import {
  parseCuratorChildren,
  useCuratorChildrenOffline,
  useCuratorPrefillVenueRows,
} from '../../api/curator';
import {
  canDeleteGroup,
  fullnessTone,
  groupCandidates,
  memberCountLabel,
  shortName,
  teacherFieldState,
  teacherLabel,
  useCuratorGroupCreate,
  useCuratorGroupDelete,
  useCuratorGroupMemberAdd,
  useCuratorGroupMemberRemove,
  useCuratorGroupPatch,
  useCuratorGroups,
  withheldInGroupHint,
  withheldOnlineHint,
  type CuratorGroup,
} from '../../api/curator-groups';
import { curatorErrorCopy } from '../../api/curator-model';
import type { GroupCandidates } from '../../api/curator-groups-model';
import { useTeachers } from '../../api/teachers';
import { Combobox } from '../../components/Combobox';
import type { ComboboxOption } from '../../components/combobox-model';
import { Badge, Button, Drawer, EmptyState, ErrorState, Field, Input, Spinner } from '../../components/ui';
import { TableWrap } from '../../components/TableWrap';
import './cabinet.css';

/**
 * «Группы» — the study-group block of the cabinet scope (contract #26.8–#26.13,
 * ADDITIVE v1.9.5, T.Z50/T.Z51). One venue at a time, exactly like the offline roster:
 * a group belongs to a ПЛОЩАДКА (the owner's binding answer), so the section exists
 * only where a venue is picked — «Все места» cannot guess where a create should land.
 * T.Z60 (owner, 2026-09-15) moved it out of the cabinet onto the «Группы» page, which
 * hands it the venue; the block itself did not change.
 *
 * THE THREE RULES the UI lives by, each one the server's, mirrored only in copy:
 *   * ≤30 members is HARD — the count line reads the api's own `memberLimit`, and a
 *     full group's «Добавить» control is disabled with the way out named («уберите
 *     кого-то»); the api's 409 GROUP_FULL stays the backstop, with the same wording.
 *   * the педагог must be of the group's venue — the picker offers only THAT venue's
 *     roster (#7.5, `?venueId=`), so a wrong-venue teacher is not offerable, and the
 *     api's 400 stays the backstop; a venue with NO педагоги locks the picker with
 *     «сначала добавьте педагогов для площадки» (T.Z61, `teacherFieldState`).
 *   * delete is the SOFT one, and only an EMPTY group offers it — the control is
 *     disabled with the copy while the roster is non-empty (409 GROUP_NOT_EMPTY).
 *
 * The candidate list for «Добавить» is BOTH of the venue's child sources (T.Z78):
 * the offline roster (#26.1) and the venue's online rows (#26.2), every entry keyed
 * by the `applicationId` the matched rows carry — the same key #26.12 assigns by. An
 * online row the matcher has not found yet has no application to name (and the api
 * refuses an application without a ЕПГУ number with a 400), so those rows are not
 * offered — and the card says so in one line ({@link withheldOnlineHint}) instead of
 * silently shrinking the list.
 *
 * T.Z82 (v1.9.14) — ONE child = ONE group: a child who already sits in another group
 * of the venue is not offered either (the row's `group` ref says so), counted into
 * {@link withheldInGroupHint}; the api's own 409 CHILD_ALREADY_IN_GROUP stays the
 * backstop. The row drawers keep the move — remove, then add — so a transfer between
 * groups is still one save.
 */
export function GroupsBlock({ venueId, venueName }: { venueId: number; venueName: string }) {
  const groups = useCuratorGroups(venueId);
  const children = useCuratorChildrenOffline(venueId);
  const rosters = useMemo(() => parseCuratorChildren(children.data), [children.data]);
  // T.Z78 — the venue's online rows (matched rows carry the assignable applicationId;
  // unmatched ones feed the hint). A FAILED read degrades to `[]`: the offline half
  // still fills the list, and the block's error copy is not worth a second ErrorState.
  const onlineRows = useCuratorPrefillVenueRows(venueId);
  const online = useMemo(() => onlineRows.data ?? [], [onlineRows.data]);

  const [form, setForm] = useState<'new' | CuratorGroup | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const openForm = (next: 'new' | CuratorGroup) => {
    setNotice(null);
    setForm(next);
  };

  const rows = groups.data ?? [];

  return (
    <section
      // T.Z81 — the `cabinet-block--groups` modifier is gone: it carried the T.Z78
      // `overflow: visible` that never applied (a page stylesheet cannot out-run
      // theme.css in the bundle, see theme.css `.card--flush`), and `.card--flush`
      // clips nothing since T.Z81 removed the clip at the source.
      className="card card--flush cabinet-block"
      data-testid="cabinet-groups"
      aria-label="Группы"
    >
      <header className="cabinet-block__head cabinet-block__head--row">
        <div>
          <h2>Группы</h2>
          <p className="cabinet-block__sub">
            {venueName} · до 30 учеников в группе, педагог — из этой же площадки. Контактов детей здесь нет.
          </p>
        </div>
        <div className="cabinet-block__actions">
          <Button
            variant="primary"
            size="sm"
            data-testid="group-add"
            onClick={() => openForm('new')}
          >
            Новая группа
          </Button>
        </div>
      </header>

      {notice !== null ? (
        <p className="cabinet-notice" role="status" data-testid="groups-notice">
          {notice}
        </p>
      ) : null}

      {groups.isPending ? (
        <Spinner label="Загружаем группы" />
      ) : groups.isError ? (
        <ErrorState
          message={curatorErrorCopy(groups.error)}
          onRetry={() => void groups.refetch()}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Групп пока нет"
          hint="Создайте группу и распределите по ней учеников площадки — до 30 человек в каждую."
        />
      ) : (
        <div className="groups-list">
          {rows.map((group) => (
            <GroupCard
              key={group.id}
              group={group}
              candidates={groupCandidates({ offline: rosters, online }, group)}
              onEdit={() => openForm(group)}
              onNotice={setNotice}
            />
          ))}
        </div>
      )}

      {form !== null ? (
        <GroupFormDrawer
          venueId={venueId}
          venueName={venueName}
          group={form === 'new' ? undefined : form}
          onClose={() => setForm(null)}
          onSaved={(message) => {
            setForm(null);
            setNotice(message);
          }}
        />
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// One group card: the roster, the add control, the edit/delete actions
// ---------------------------------------------------------------------------

function GroupCard({
  group,
  candidates,
  onEdit,
  onNotice,
}: {
  group: CuratorGroup;
  /** T.Z78 — both sources folded: the offerable options plus the withheld count. */
  candidates: GroupCandidates;
  onEdit: () => void;
  onNotice: (message: string) => void;
}) {
  const add = useCuratorGroupMemberAdd();
  const remove = useCuratorGroupMemberRemove();
  const del = useCuratorGroupDelete();
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const full = group.memberCount >= group.memberLimit;
  const deletable = canDeleteGroup(group);
  const options: ComboboxOption[] = candidates.options.map((candidate) => ({
    value: String(candidate.applicationId),
    label: candidate.label,
  }));
  const withheldHint = withheldOnlineHint(candidates.withheld);
  // T.Z82 — the children the list no longer offers because ONE child = ONE group:
  // they are already in another group, and the api would answer the second membership
  // with 409 CHILD_ALREADY_IN_GROUP. Said out loud, not silently shrunk.
  const inGroupHint = withheldInGroupHint(candidates.inOtherGroup);

  const submitAdd = () => {
    const applicationId = Number(draft);
    if (!Number.isInteger(applicationId) || applicationId < 1) return;
    setError(null);
    add.mutate(
      { groupId: group.id, applicationId },
      {
        onSuccess: (updated) => {
          setDraft('');
          onNotice(`Добавлено в «${updated.name}»: ${updated.memberCount} из ${updated.memberLimit}.`);
        },
        onError: (cause) => setError(curatorErrorCopy(cause)),
      },
    );
  };

  return (
    <article className="groups-card" data-testid="group-card" data-group-id={group.id}>
      <header className="groups-card__head">
        <div>
          <h3 className="groups-card__title" data-testid={`group-name-${group.id}`}>
            {group.name}
          </h3>
          <p className="groups-card__sub" data-testid={`group-teacher-${group.id}`}>
            {teacherLabel(group)}
          </p>
        </div>
        <div className="groups-card__meta">
          <Badge
            tone={fullnessTone(group)}
            testId={`group-count-${group.id}`}
            title="Состав группы и жёсткий лимит (задаёт сервер)"
          >
            {memberCountLabel(group)}
          </Badge>
          <Button variant="secondary" size="sm" data-testid={`group-edit-${group.id}`} onClick={onEdit}>
            Изменить
          </Button>
          {confirming ? (
            <span className="groups-confirm" data-testid={`group-confirm-${group.id}`}>
              <Button
                variant="danger"
                size="sm"
                data-testid={`group-delete-yes-${group.id}`}
                loading={del.isPending}
                disabled={!deletable}
                onClick={() =>
                  del.mutate(group.id, {
                    onSuccess: () => onNotice(`Группа «${group.name}» удалена.`),
                    onError: (cause) => {
                      setConfirming(false);
                      setError(curatorErrorCopy(cause));
                    },
                  })
                }
              >
                Точно удалить
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setConfirming(false)}>
                Отмена
              </Button>
            </span>
          ) : (
            <Button
              variant="danger"
              size="sm"
              data-testid={`group-delete-${group.id}`}
              disabled={!deletable}
              title={deletable ? undefined : 'Сначала уберите из группы всех участников.'}
              onClick={() => setConfirming(true)}
            >
              Удалить
            </Button>
          )}
        </div>
      </header>

      {group.members.length === 0 ? (
        <p className="groups-card__empty" data-testid={`group-empty-${group.id}`}>
          В группе пока никого нет — добавьте ученика из офлайн-списка или онлайн-набора площадки.
        </p>
      ) : (
        <TableWrap>
          <table className="data" data-testid={`group-table-${group.id}`}>
            <thead>
              <tr>
                <th scope="col">ФИО</th>
                <th scope="col">Номер заявления</th>
                <th scope="col" aria-label="Убрать из группы" />
              </tr>
            </thead>
            <tbody>
              {group.members.map((member) => (
                <tr key={member.applicationId} data-testid={`group-member-${member.applicationId}`}>
                  <td className="cell-primary">{member.fullName}</td>
                  <td>{member.pguId === '' ? '—' : member.pguId}</td>
                  <td className="cell-compact">
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`group-member-remove-${group.id}-${member.applicationId}`}
                      disabled={remove.isPending}
                      onClick={() =>
                        remove.mutate(
                          { groupId: group.id, applicationId: member.applicationId },
                          {
                            onError: (cause) => setError(curatorErrorCopy(cause)),
                          },
                        )
                      }
                    >
                      Убрать
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}

      <footer className="groups-card__add">
        <Combobox
          testId={`group-member-candidate-${group.id}`}
          value={draft}
          options={options}
          onChange={setDraft}
          placeholder={full ? 'Группа заполнена' : 'Найдите ученика офлайн-списка или онлайн-набора'}
          emptyText="Ученик не найден"
          clearLabel="Отменить выбор"
          ariaLabel={`Добавить ученика в «${group.name}»`}
          resultsLabel={(count) => `Найдено учеников: ${count}`}
          disabled={full || add.isPending}
        />
        <Button
          variant="secondary"
          size="sm"
          data-testid={`group-member-add-${group.id}`}
          disabled={full || draft === '' || add.isPending}
          onClick={submitAdd}
        >
          Добавить ученика
        </Button>
        {full ? (
          <span className="groups-card__hint" data-testid={`group-full-${group.id}`}>
            Лимит {memberCountLabel(group)} — уберите кого-то, чтобы добавить нового.
          </span>
        ) : null}
        {withheldHint !== null ? (
          // T.Z78 — the online rows that cannot be members YET, named rather than
          // silently dropped: the api's own rule (#26.12 — the member is an
          // application with a ЕПГУ number), and the matcher as the way out.
          <p className="groups-card__hint" role="status" data-testid={`group-online-hint-${group.id}`}>
            {withheldHint}
          </p>
        ) : null}
        {inGroupHint !== null ? (
          // ADDITIVE (v1.9.14, T.Z82) — the children already in ANOTHER group, named
          // rather than silently dropped from the list.
          <p className="groups-card__hint" role="status" data-testid={`group-inother-hint-${group.id}`}>
            {inGroupHint}
          </p>
        ) : null}
        {error !== null ? (
          <p className="profile__error" role="alert" data-testid={`group-error-${group.id}`}>
            {error}
          </p>
        ) : null}
      </footer>
    </article>
  );
}

// ---------------------------------------------------------------------------
// The create/edit drawer (#26.9 / #26.10)
// ---------------------------------------------------------------------------

function GroupFormDrawer({
  venueId,
  venueName,
  group,
  onClose,
  onSaved,
}: {
  venueId: number;
  venueName: string;
  /** `undefined` = the create form. */
  group?: CuratorGroup;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const isEdit = group !== undefined;
  const create = useCuratorGroupCreate();
  const patch = useCuratorGroupPatch();
  // The same-venue rule, in the picker's own options: only THIS venue's педагоги are
  // offered (#7.5 `?venueId=`), so a wrong-venue id is not reachable from the form.
  const teachers = useTeachers({ venueId, pageSize: 200 });
  const [name, setName] = useState(group?.name ?? '');
  const [teacherId, setTeacherId] = useState(group?.teacher !== undefined && group.teacher !== null ? String(group.teacher.id) : '');
  const [error, setError] = useState<string | null>(null);

  const teacherOptions: ComboboxOption[] = (teachers.data?.teachers ?? []).map((teacher) => ({
    value: String(teacher.id),
    label: shortName(teacher.fullName),
    hint: teacher.venueName,
  }));

  // T.Z61 — an empty LOADED roster locks the picker and says why: the venue has no
  // педагоги to pick from, so a create that wants one has to start at the registry
  // («Педагоги»). A group whose assigned педагог is no longer listed (the registry
  // moved them to another venue) keeps its value on screen and simply cannot re-pick
  // here — the api's own `ON DELETE SET NULL` already cleared the deleted ones.
  const teacherField = teacherFieldState({
    pending: teachers.isPending,
    teacherCount: teacherOptions.length,
  });

  const trimmed = name.trim();
  const canSubmit =
    trimmed.length > 0 &&
    (group === undefined || trimmed !== group.name || teacherId !== currentTeacherId(group));

  const submit = () => {
    setError(null);
    const parsedTeacherId = teacherId === '' ? null : Number(teacherId);
    if (isEdit && group !== undefined) {
      const body: { name?: string; teacherId?: number | null } = {};
      if (trimmed !== group.name) body.name = trimmed;
      if (parsedTeacherId !== currentTeacherIdOrNull(group)) body.teacherId = parsedTeacherId;
      if (Object.keys(body).length === 0) {
        onClose();
        return;
      }
      patch.mutate(
        { groupId: group.id, patch: body },
        {
          onSuccess: (updated) => onSaved(`Группа «${updated.name}» сохранена.`),
          onError: (cause) => setError(curatorErrorCopy(cause)),
        },
      );
      return;
    }
    create.mutate(
      {
        venueId,
        name: trimmed,
        ...(parsedTeacherId === null ? {} : { teacherId: parsedTeacherId }),
      },
      {
        onSuccess: (created) => onSaved(`Группа «${created.name}» создана.`),
        onError: (cause) => setError(curatorErrorCopy(cause)),
      },
    );
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={isEdit ? 'Изменить группу' : 'Новая группа'}
      description={`${venueName} · название и педагог площадки; состав задаётся в карточке группы.`}
    >
      <form
        className="groups-form"
        data-testid="group-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (canSubmit) submit();
        }}
      >
        <Field label="Название" htmlFor="group-name" required hint="Например: Группа 1 (Python 8-9)">
          <Input
            id="group-name"
            data-testid="group-name-input"
            value={name}
            maxLength={200}
            onChange={(event) => setName(event.currentTarget.value)}
          />
        </Field>
        <Field
          label="Педагог"
          htmlFor="group-teacher"
          // The lock's own line replaces the standing «только педагоги этой площадки» —
          // two sentences under one locked field would read as a contradiction.
          hint={teacherField.hint === null ? 'Только педагоги этой площадки — список ведёт реестр «Педагоги».' : undefined}
        >
          <Combobox
            id="group-teacher"
            testId="group-teacher-input"
            value={teacherId}
            options={teacherOptions}
            onChange={setTeacherId}
            placeholder="педагог не назначен"
            emptyText="Педагог не найден"
            clearLabel="Не назначать педагога"
            ariaLabel="Педагог группы"
            resultsLabel={(count) => `Найдено педагогов: ${count}`}
            disabled={teacherField.disabled}
          />
        </Field>
        {teacherField.hint !== null ? (
          // Rendered OUTSIDE the Field (whose hint slot takes no test id) so the
          // acceptance click-path can pin the exact wording of the lock.
          <p className="field__hint" role="status" data-testid="group-teacher-empty-hint">
            {teacherField.hint}
          </p>
        ) : null}
        {error !== null ? (
          <p className="profile__error" role="alert" data-testid="group-form-error">
            {error}
          </p>
        ) : null}
        <footer className="groups-form__footer">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" size="sm" type="submit" loading={create.isPending || patch.isPending} disabled={!canSubmit} data-testid="group-save">
            {isEdit ? 'Сохранить' : 'Создать'}
          </Button>
        </footer>
      </form>
    </Drawer>
  );
}

function currentTeacherId(group: CuratorGroup): string {
  return group.teacher === null ? '' : String(group.teacher.id);
}

function currentTeacherIdOrNull(group: CuratorGroup): number | null {
  return group.teacher === null ? null : group.teacher.id;
}
